// noinspection ES6UnusedImports

import type {
    FirebaseApp,
} from 'firebase/app';
import type {
    Firestore,
    DocumentData,
    DocumentSnapshot,
    QuerySnapshot,
    FirestoreError,
} from 'firebase/firestore';
import {
    getFirestore,
    doc,
    getDoc,
    addDoc,  // auto-generated Id
    setDoc,  // own Id
    updateDoc, // change only parts of doc (one property?)
    deleteDoc,
    collection,
    getDocs,
    // serverTimestamp,
    // Bytes,
    query as firebaseQuery,
    where,
    onSnapshot
} from 'firebase/firestore'
import type {User} from 'firebase/auth';
import type {Unsubscribe} from 'firebase/firestore';
import type {ProVersionDocDB} from '$lib/classes/stores/pro-version';
import type {ReviewCountDoc} from '$lib/classes/stores/review-count';
import type {SubscriptionDoc} from '$lib/classes/stores/subscription';
import type {UserSettingsDoc} from '$lib/classes/stores/user-settings';
import {Action, type UserWordChange} from '$lib/classes/data/user-word-change';
import {UserWordConverter, type UserWordJson} from '$lib/classes/data/user-word-converter';
import {UserWord} from '$lib/classes/data/user-word';
import {convertBetweenWordIdAndDbKey} from '$lib/utils/userWords/convertBetweenWordIdAndDbKey';
import type {SurveyRecord} from '$lib/classes/_types/survey';
import type {TestimonialRecord} from '$lib/classes/_types/testimonialRecord';
import type {MobileSubscriptionJson} from '$lib/classes/services/mobile-subscription-manager.svelte';

export class Database {
    // CHANGE ALSO IN CLOUD FUNCTION .auth.user().onDelete() & Manage-Users !!!
    private readonly DB_COLLECTION_MAIN_USER_WORDS = 'user-words-v1' // docId = uid
    private readonly DB_COLLECTION_SUB_USER_WORDS = 'user-words' // docId = wordId
    private readonly DB_COLLECTION_USER_SETTINGS = 'user-stgs'
    private readonly DB_COLLECTION_PRO_VERSION = 'pro-version'
    private readonly DB_COLLECTION_SUBSCRIPTION = 'subscription'
    private readonly DB_COLLECTION_MOBILE_SUBSCRIPTION = 'mobile-subscription'
    private readonly DB_COLLECTION_REVIEW_COUNT = 'review-count'
    private readonly DB_COLLECTION_NEXT_FEATURE = 'next-feature'
    private readonly DB_COLLECTION_TESTIMONIAL = 'testimonial'

    get DB_PATH_REVIEW_COUNT() {
        return this.DB_COLLECTION_REVIEW_COUNT + '/' + new Date().getFullYear() + '/' + this.DB_COLLECTION_REVIEW_COUNT
    }

    private readonly db: Firestore

    constructor(
        firebaseApp: FirebaseApp,
        private readonly userWordConverter: UserWordConverter
    ) {
        this.db = getFirestore(firebaseApp)
    }

    private async getDoc(collection: string, docId: string) {
        const docRef = doc(this.db, collection, docId)
        const response = await getDoc(docRef)
        if (response.exists()) {
            return response.data()
        } else {
            return null
        }
    }

    // auto-generated Id
    private async addDoc(path: string, docObj: DocumentData) {
        const collectionRef = collection(this.db, path)
        return await addDoc(collectionRef, docObj)
    }

    private async setDoc(collection: string, docId: string, docObj: DocumentData) {
        const docRef = doc(this.db, collection, docId)
        await setDoc(docRef, docObj)
    }

    //merge compared to update >> no error if doc doesn't exist, it's then created
    private async setDocMerge(collection: string, docId: string, docObj: DocumentData) {
        const docRef = doc(this.db, collection, docId)
        await setDoc(docRef, docObj, {merge: true})
    }

    // async updateDoc(collection: string, docId: string, partialDocObj: DocumentData) {
    //     const docRef = doc(this.db, collection, docId)
    //     await updateDoc(docRef, partialDocObj)
    // }

    private async deleteDoc(collection: string, docId: string) {
        await deleteDoc(doc(this.db, collection, docId));
    }

    // async getDocs(collectionId: string) {
    //     const querySnapshot = await getDocs(collection(this.db, collectionId))
    //     return querySnapshot.docs.map(doc => doc.data())
    // }

    private subscribeDoc(collection: string, docId: string, callback: (doc: DocumentData | null) => void): Promise<Unsubscribe> {
        return new Promise((resolve, reject) => {
            const docRef = doc(this.db, collection, docId)
            const unsubscribe = onSnapshot(
                docRef,
                (snapshot) => {
                    if (snapshot.exists()) {
                        callback(snapshot.data())
                    } else {
                        callback(null)
                    }
                    resolve(unsubscribe)
                },
                (error) => {
                    reject(new Error('database.subscribeDoc', {cause: error}))
                }
            )
        })
    }

    private subscribeCollection(collectionPath: string, callback: (querySnapshot: QuerySnapshot) => void): Promise<Unsubscribe> {
        return new Promise((resolve, reject) => {
            // QUESTION is the query even needed - just collection would be enough?
            const q = firebaseQuery(collection(this.db, collectionPath))
            const unsubscribeCollection = onSnapshot(
                q,
                (querySnapshot) => {
                    callback(querySnapshot)
                    resolve(unsubscribeCollection)
                },
                (error: FirestoreError) => {
                    reject(new Error('database.subscribeCollection', {cause: error}))
                })
        })
    }

    // PUBLIC API
    async subscribeUserWordsCollection(uid: User['uid'], callback: (userWordChanges: UserWordChange[]) => void) {
        const path = this.DB_COLLECTION_MAIN_USER_WORDS + '/' + uid + '/' + this.DB_COLLECTION_SUB_USER_WORDS
        return await this.subscribeCollection(
            path,
            (querySnapshot) => {
                const docChanges = querySnapshot.docChanges()
                const userWordChanges = docChanges.map(change => {
                    const json = change.doc.data() as UserWordJson
                    const userWord = this.userWordConverter.fromJson(json)
                    const type = change.type
                    const action = type === 'added' ? Action.Added : type === 'modified' ? Action.Modified : Action.Removed
                    const changedLocally = change.doc.metadata.hasPendingWrites
                    return {
                        userWord,
                        action,
                        changedLocally
                    }
                })
                callback(userWordChanges)
            }
        )
    }

    async setUserWordsDoc(uid: User['uid'], userWord: UserWord) {
        const path = this.DB_COLLECTION_MAIN_USER_WORDS + '/' + uid + '/' + this.DB_COLLECTION_SUB_USER_WORDS
        const docId = convertBetweenWordIdAndDbKey(userWord.id)
        const docObj = this.userWordConverter.toJson(userWord)
        return await this.setDocMerge(path, docId, docObj)
    }

    async deleteUserWord(uid: User['uid'], userWord: UserWord) {
        const path = this.DB_COLLECTION_MAIN_USER_WORDS + '/' + uid + '/' + this.DB_COLLECTION_SUB_USER_WORDS
        const docId = convertBetweenWordIdAndDbKey(userWord.id)
        return await this.deleteDoc(path, docId)
    }

    async getUserSettingsDoc(uid: User['uid']) {
        return await this.getDoc(this.DB_COLLECTION_USER_SETTINGS, uid) as UserSettingsDoc | null
    }

    async setUserSettingsDoc(uid: User['uid'], doc: Partial<UserSettingsDoc>) {
        await this.setDocMerge(this.DB_COLLECTION_USER_SETTINGS, uid, doc)
    }

    async subscribeUserSettingsDoc(uid: User['uid'], callback: (doc: UserSettingsDoc | null) => void) {
        return await this.subscribeDoc(this.DB_COLLECTION_USER_SETTINGS, uid, (doc: DocumentData | null) => {
            callback(doc as UserSettingsDoc | null)
        })
    }

    async getProVersionDoc(uid: string) {
        return await this.getDoc(this.DB_COLLECTION_PRO_VERSION, uid) as ProVersionDocDB | null
    }

    async setProVersionDoc(uid: User['uid'], doc: ProVersionDocDB) {
        await this.setDoc(this.DB_COLLECTION_PRO_VERSION, uid, doc)
    }

    async subscribeProVersionDoc(uid: User['uid'], callback: (doc: ProVersionDocDB | null) => void) {
        return await this.subscribeDoc(this.DB_COLLECTION_PRO_VERSION, uid, (doc: DocumentData | null) => {
            callback(doc as ProVersionDocDB | null)
        })
    }

    async getReviewCountDoc(uid: User['uid']) {
        return await this.getDoc(this.DB_PATH_REVIEW_COUNT, uid) as ReviewCountDoc | null
    }

    async setReviewCountDoc(uid: User['uid'], doc: ReviewCountDoc) {
        await this.setDocMerge(this.DB_PATH_REVIEW_COUNT, uid, doc)
    }

    async subscribeSubscriptionDoc(uid: User['uid'], callback: (doc: SubscriptionDoc | null) => void) {
        return await this.subscribeDoc(this.DB_COLLECTION_SUBSCRIPTION, uid, (doc: DocumentData | null) => {
            callback(doc as SubscriptionDoc | null)
        })
    }

    async subscribeMobileSubscriptionDoc(uid: User['uid'], callback: (doc: MobileSubscriptionJson | null) => void) {
        return await this.subscribeDoc(this.DB_COLLECTION_MOBILE_SUBSCRIPTION, uid, (doc: DocumentData | null) => {
            callback(doc as MobileSubscriptionJson | null)
        })
    }

    async getSurveyRecord(uid: User['uid']): Promise<SurveyRecord | null> {
        return await this.getDoc(this.DB_COLLECTION_NEXT_FEATURE, uid) as SurveyRecord | null
    }

    async setSurveyRecord(uid: User['uid'], doc: SurveyRecord): Promise<void> {
        await this.setDocMerge(this.DB_COLLECTION_NEXT_FEATURE, uid, doc)
    }

    async setTestimonial(doc: TestimonialRecord): Promise<void> {
        await this.addDoc(this.DB_COLLECTION_TESTIMONIAL, doc)
    }
}